import {
  TimestampString,
  TranslationMap_Key,
  UUIDString,
  deleteTranslationMap,
  getTranslationMapByDestination,
  getTranslationMapsByResourceType,
  getTranslationMapsBySourceType,
  upsertTranslationMap,
} from '@principle-theorem/principle-db-connector';
import {
  DocumentData,
  INamedDocument,
  SerialisedData,
  SystemActors,
  WithPrimaryKey,
  WithRef,
  getError,
  serialise,
  toTimestamp,
  unserialise,
  type DocumentReference,
} from '@principle-theorem/shared';
import { first } from 'lodash';
import { PracticeMigration } from '../practice-migrations';
import {
  ITranslationMapHandler,
  IPracticeMigration,
  ITranslationMap,
  ISourceLinkOption,
} from '@principle-theorem/practice-migrations/interfaces';

export class TranslationMapHandler implements ITranslationMapHandler {
  constructor(private _migration: WithRef<IPracticeMigration>) {}

  async upsert(translationMap: ITranslationMap): Promise<void> {
    const dataConnect = await PracticeMigration.dataConnect(this._migration);
    const serialised = serialise(translationMap);

    try {
      /* eslint-disable no-null/no-null */
      await upsertTranslationMap(dataConnect, {
        id: serialised.id,
        sourceIdentifier: serialised.sourceIdentifier,
        sourceLabel: serialised.sourceLabel ?? null,
        sourceLink: serialised.sourceLink ?? null,
        resourceType: serialised.resourceType,
        destinationValue: serialised.destinationValue ?? null,
        destinationIdentifier: serialised.destinationIdentifier ?? null,
        associatedValue: serialised.associatedValue ?? null,
        isManualInput: serialised.isManualInput ?? null,
      });
      /* eslint-enable no-null/no-null */
    } catch (error) {
      throw new Error(`Error Upserting Translation Map: ${getError(error)}`);
    }
  }

  async getByType<T extends object = object, R = unknown>(
    resourceType: string
  ): Promise<WithPrimaryKey<ITranslationMap<T, R>>[]> {
    const dataConnect = await PracticeMigration.dataConnect(this._migration);
    const data = await getTranslationMapsByResourceType(dataConnect, {
      resourceType,
      limit: 5000,
    });
    return data.data.translationMaps.map((translationMap) =>
      this.unserialise<T, R>(translationMap)
    );
  }

  async getDestination<T extends object = object, R = unknown>(
    sourceIdentifier: string,
    resourceType: string
  ): Promise<DocumentReference<T> | undefined> {
    const translationMap = await this.getBySource<T, R>(
      sourceIdentifier,
      resourceType
    );

    if (!translationMap?.destinationIdentifier) {
      return;
    }

    return unserialise<DocumentReference<T>>(
      translationMap.destinationIdentifier
    );
  }

  async getBySource<T extends object = object, R = unknown>(
    sourceIdentifier: string,
    resourceType: string
  ): Promise<WithPrimaryKey<ITranslationMap<T, R>> | undefined> {
    const dataConnect = await PracticeMigration.dataConnect(this._migration);
    const data = await getTranslationMapsBySourceType(dataConnect, {
      sourceIdentifier,
      resourceType,
    });

    const translationMap = first(data.data.translationMaps);

    if (!translationMap) {
      return;
    }

    return this.unserialise<T, R>(translationMap);
  }

  async getByDestination<T extends object = DocumentData, R = unknown>(
    destinationIdentifier: DocumentReference<T>,
    resourceType: string
  ): Promise<WithPrimaryKey<ITranslationMap<T, R>> | undefined> {
    const dataConnect = await PracticeMigration.dataConnect(this._migration);
    const data = await getTranslationMapByDestination(dataConnect, {
      destinationIdentifier: serialise(destinationIdentifier),
      resourceType,
    });
    const translationMap = first(data.data.translationMaps);

    if (!translationMap) {
      return;
    }

    return this.unserialise<T, R>(translationMap);
  }

  async deleteRecord(
    translationMap: WithPrimaryKey<ITranslationMap>
  ): Promise<void> {
    const dataConnect = await PracticeMigration.dataConnect(this._migration);
    await deleteTranslationMap(dataConnect, { id: translationMap.id });
  }

  unserialise<T extends object = DocumentData, R = unknown>(
    translationMap: TranslationMapResponse
  ): WithPrimaryKey<ITranslationMap<T, R>> {
    if (!translationMap) {
      throw new Error('Translation map not found');
    }
    return {
      ...translationMap,
      id: translationMap.id,
      sourceLabel: translationMap.sourceLabel ?? undefined,
      isManualInput: translationMap.isManualInput ?? undefined,
      associatedValue: translationMap.associatedValue
        ? unserialise<string | INamedDocument<T>>(
            translationMap.associatedValue as SerialisedData<
              string | INamedDocument<T>
            >
          )
        : undefined,
      destinationValue: translationMap.destinationValue
        ? (unserialise(translationMap.destinationValue) as { value: R })
        : undefined,
      destinationIdentifier: translationMap.destinationIdentifier
        ? (unserialise(
            translationMap.destinationIdentifier
          ) as DocumentReference<T>)
        : undefined,
      sourceLink: translationMap.sourceLink
        ? (unserialise(translationMap.sourceLink) as ISourceLinkOption)
        : undefined,
      createdAt: toTimestamp(translationMap.createdAt),
      createdBy: SystemActors.Migration,
      updatedAt: toTimestamp(translationMap.updatedAt),
      updatedBy: SystemActors.Migration,
    };
  }
}

type TranslationMapResponse = {
  id: UUIDString;
  sourceIdentifier: string;
  sourceLabel?: string | null;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  sourceLink?: unknown | null;
  resourceType: string;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  destinationValue?: unknown | null;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  destinationIdentifier?: unknown | null;
  // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
  associatedValue?: unknown | null;
  isManualInput?: boolean | null;
  createdAt: TimestampString;
  createdBy?: string | null;
  updatedAt: TimestampString;
  updatedBy?: string | null;
} & TranslationMap_Key;
